import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { ToastType } from 'src/app/constants/toast-type';
import { BasePaths } from 'src/app/constants/routes';
import { ApiService } from './api.service';
import { ToastService } from './toast.service';
import { IOperatingMode, PassthroughBearTokenPayload, OperatingMode, OTPResponse, SMSVerifyPayload, TokenResponse, UpdatePasswordCriteria, IdentityServerRefresh, IdentityServerGrantBase } from 'src/app/models/auth';
import { Agent, AgentLogin, AgentLoginCredentials, AgentMemberLoginResponse } from 'src/app/models/agent';
import { BaseMemberSession, LoginPayload, MemberAuthorizationResult, MemberLogin, MemberSession, StartSelectedMemberSession } from '../models/member';
import { ResetPasswordRequestResponse, ResetPasswordRequestPayload, BaseMemberSessionList, AgentCreatePassword } from '../models/password';
import { AdminDirectSystemLocation } from '../constants/admin-direct-system';
import { HelperService } from './helper.service';
import { RegisterCriteria } from '../models/register-criteria';
import { SessionService } from './storage/session.service';
import { EncryptionService } from './encryption.service';
import { PassEncryption } from '../models/encryption';
import { Authenticator, AuthenticatorInfo, AuthenticatorQR, AuthenticatorRequest, MultiFactorAuthenticator } from '../models/authenticator';

const controllerRoute = '/auth';
const agentControllerRoute = '/agents';
const memberControllerRoute = '/members';
const myProfileRoute = '/myprofile';

@Injectable()
export class AuthService {

    private adminDirectSystemLocation = AdminDirectSystemLocation;

    constructor(
        private apiService: ApiService,
        private router: Router,
        private sessionService: SessionService,
        private toast: ToastService,
        private helperService: HelperService,
        private encryptionService: EncryptionService
    ) {
    }

    public getOperatingMode(): Observable<OperatingMode> {
        return this.apiService.get(`${controllerRoute}/operating`).pipe(
            map((mode: IOperatingMode) => {
                this.sessionService.checkOpertingVersion(mode.version);
                this.sessionService.session.operatingMode = mode;
                this.sessionService.setSession();
                return mode;
            }),
        );
    }

    public captchaAuthentication(payload: TokenResponse): Observable<TokenResponse> {
        return this.apiService.postNoSpinner(`${controllerRoute}/captcha-authentication`, payload).pipe(
            map((response: TokenResponse) => {
                return response;
            }),
        );
    }

    public agentLogin(payload: AgentLoginCredentials): Observable<AuthenticatorInfo> {
        return from(this.encryptionService.encryptPassword(payload.password)).pipe(
            switchMap((encryptedPassword: PassEncryption) => {
                payload.password = null;
                payload.passEncryption.a1 = encryptedPassword.a1
                payload.passEncryption.m1 = encryptedPassword.m1
                payload.passEncryption.m2 = encryptedPassword.m2
                return this.apiService.post(`${controllerRoute}/credentials/agent`, payload).pipe(
                    map((response: AuthenticatorInfo) => {
                        this.apiService.setAuthToken(response.accessToken, response.refreshToken);
                        return response;
                    }));
            })
        );
    }

    private agentLoginVerified(agent: Agent, setAuthToken: boolean): void {
        this.sessionService.session.isAgent = !!agent;
        this.sessionService.session.agent = agent;
        this.sessionService.session.selectedBrokerId = this.sessionService.session.agent.brokerID;
        if (setAuthToken) {
            this.apiService.setAuthToken(agent.accessToken, agent.refreshToken);
        }
        if (!agent.mustResetPassword) {
            this.sessionService.session.isLoggedIn = true;
        } else {
            this.toast.buildToastMessage(ToastType.warning, 'Password Expired!', 'Please enter a new password.');
        }
        this.sessionService.setSession();
    }

    public setAgentMemberLogin(payload: MemberLogin): Observable<AgentMemberLoginResponse> {
        return this.apiService.post(`${controllerRoute}/select-agent-member-login`, payload).pipe(
            map((agentMemberLoginResponse: AgentMemberLoginResponse) => {
                return agentMemberLoginResponse;
            }),
        );
    }

    public agentMemberLogout(): Observable<null> {
        return this.apiService.post(`${controllerRoute}/agent/member/logout`, null).pipe(
            map((response: any) => {
                this.sessionService.clearMemberSession();
                return null;
            }),
        );
    }

    public apiLogout(): Observable<null> {
        let l_Path: string;

        if (this.sessionService.session.isAgent) {
            l_Path = 'agent/logout';
        } else {
            l_Path = 'logout';
        }

        return this.apiService.postNoSpinner(`${controllerRoute}/${l_Path}`, null).pipe(
            map((response: any) => {
                return null;
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    public logout(): void {
        let l_RedirectPath: string;

        if (this.sessionService.session.isAgent) {
            l_RedirectPath = BasePaths.Counselors;
        } else if (this.sessionService.session.isFirstResponder) {
            l_RedirectPath = `${BasePaths.FirstResponders}/${this.sessionService.session.firstResponderComCode}`;
        } else {
            l_RedirectPath = BasePaths.Members;
        }

        this.logoutNavigate(l_RedirectPath);
    }

    private logoutNavigate(path: string): void {
        const l_IsIEOrEdge = /msie\s|trident\/|edge|edg\//i.test(window.navigator.userAgent);
        this.sessionService.logout();
        this.apiService.cancelTimer();

        if (l_IsIEOrEdge) {
            this.router.navigateByUrl(path).then(() => {
                location.reload();
            });
        } else {
            this.router.navigateByUrl(path);
        }
    }

    // Reset PW response rules:
    // if multiple members it requires member select
    // if single member the api only retrieves message 
    // if theres a company code, its a first responder screen
    // public resetMemberPasswordRequest(payload: ResetPasswordRequestPayload): Observable<ResetPasswordRequestResponse> {
    //     return this.apiService.post(`${controllerRoute}/reset-member-password`, payload).pipe(
    //         map((ResetPasswordResponse: ResetPasswordRequestResponse) => {
    //             return ResetPasswordResponse;
    //         }),
    //     );
    // }

    public resetMemberPassword(payload: LoginPayload): Observable<BaseMemberSessionList> {
        return from(this.encryptionService.encryptPassword(payload.password)).pipe(
            switchMap((encryptedPassword: PassEncryption) => {
                payload.password = null;
                payload.passEncryption.a1 = encryptedPassword.a1
                payload.passEncryption.m1 = encryptedPassword.m1
                payload.passEncryption.m2 = encryptedPassword.m2
                return this.apiService.post(`${controllerRoute}/reset-password`, payload).pipe(
                    map(response => response as BaseMemberSessionList));
            }));
    }


    public resetAgentPassword(payload: AgentCreatePassword): Observable<AgentLogin> {
        return from(this.encryptionService.encryptPassword(payload.newPassword)).pipe(
            switchMap((existingEnc: PassEncryption) => {
                return from(this.encryptionService.encryptPassword(payload.newPassword)).pipe(
                    switchMap((newEnc: PassEncryption) => {
                        return from(this.encryptionService.encryptPassword(payload.newPasswordConfirm)).pipe(
                            switchMap((confirmEnc: PassEncryption) => {

                                payload.existingPassword = null;
                                payload.newPassword = null;
                                payload.newPasswordConfirm = null;

                                payload.existingPasswordEncryption.a1 = existingEnc.a1;
                                payload.existingPasswordEncryption.m1 = existingEnc.m1;
                                payload.existingPasswordEncryption.m2 = existingEnc.m2;

                                payload.newPasswordEncryption.a1 = newEnc.a1;
                                payload.newPasswordEncryption.m1 = newEnc.m1;
                                payload.newPasswordEncryption.m2 = newEnc.m2;

                                payload.newPasswordConfirmEncryption.a1 = confirmEnc.a1;
                                payload.newPasswordConfirmEncryption.m1 = confirmEnc.m1;
                                payload.newPasswordConfirmEncryption.m2 = confirmEnc.m2;
                                return this.apiService.post(`${controllerRoute}/create-agent-password`, payload).pipe(
                                    map((agentLogin: AgentLogin) => {
                                        agentLogin.agent.adminDirectSystem = payload.adminDirectSystem;
                                        agentLogin.agent.agentCity = payload.agentCity;
                                        agentLogin.agent.agentState = payload.agentState;
                                        this.agentLoginVerified(agentLogin.agent, false);
                                        return agentLogin;
                                    })
                                );
                            })
                        );
                    })
                );
            })
        );
    }


    public updateMemberPassword(payload: UpdatePasswordCriteria): Observable<void> {
        return from(this.encryptionService.encryptPassword(payload.existingPassword)).pipe(
            switchMap((existingEnc: PassEncryption) => {
                return from(this.encryptionService.encryptPassword(payload.newPassword)).pipe(
                    switchMap((newEnc: PassEncryption) => {
                        return from(this.encryptionService.encryptPassword(payload.newPasswordConfirm)).pipe(
                            switchMap((confirmEnc: PassEncryption) => {

                                payload.existingPassword = null;
                                payload.newPassword = null;
                                payload.newPasswordConfirm = null;

                                payload.existingPasswordEncryption.a1 = existingEnc.a1;
                                payload.existingPasswordEncryption.m1 = existingEnc.m1;
                                payload.existingPasswordEncryption.m2 = existingEnc.m2;

                                payload.newPasswordEncryption.a1 = newEnc.a1;
                                payload.newPasswordEncryption.m1 = newEnc.m1;
                                payload.newPasswordEncryption.m2 = newEnc.m2;

                                payload.newPasswordConfirmEncryption.a1 = confirmEnc.a1;
                                payload.newPasswordConfirmEncryption.m1 = confirmEnc.m1;
                                payload.newPasswordConfirmEncryption.m2 = confirmEnc.m2;

                                return this.apiService.post(`${myProfileRoute}/update-password`, payload).pipe(
                                    map(() => {
                                        this.toast.buildToastMessage(ToastType.success, '', 'Password Updated!');
                                    })
                                );
                            })
                        );
                    })
                );
            })
        );
    }

    public updateAgentPassword(payload: UpdatePasswordCriteria): Observable<any> {
        return from(this.encryptionService.encryptPassword(payload.existingPassword)).pipe(
            switchMap((existingEnc: PassEncryption) => {
                return from(this.encryptionService.encryptPassword(payload.newPassword)).pipe(
                    switchMap((newEnc: PassEncryption) => {
                        return from(this.encryptionService.encryptPassword(payload.newPasswordConfirm)).pipe(
                            switchMap((confirmEnc: PassEncryption) => {

                                payload.existingPassword = null;
                                payload.newPassword = null;
                                payload.newPasswordConfirm = null;

                                payload.existingPasswordEncryption.a1 = existingEnc.a1;
                                payload.existingPasswordEncryption.m1 = existingEnc.m1;
                                payload.existingPasswordEncryption.m2 = existingEnc.m2;

                                payload.newPasswordEncryption.a1 = newEnc.a1;
                                payload.newPasswordEncryption.m1 = newEnc.m1;
                                payload.newPasswordEncryption.m2 = newEnc.m2;

                                payload.newPasswordConfirmEncryption.a1 = confirmEnc.a1;
                                payload.newPasswordConfirmEncryption.m1 = confirmEnc.m1;
                                payload.newPasswordConfirmEncryption.m2 = confirmEnc.m2;

                                return this.apiService.post(`${agentControllerRoute}/update-password`, payload).pipe(map(() => {
                                    this.toast.buildToastMessage(ToastType.success, '', 'Password Updated!');
                                    return;
                                })
                                );
                            })
                        );
                    })
                );
            })
        );
    }

    public verifyAgentToken(token: string): Observable<AgentLogin> {
        const serializeToken = this.apiService.serializeString(token);
        return this.apiService.get(`${agentControllerRoute}/${serializeToken}/verify`).pipe(
            map((response: AgentLogin) => {
                this.apiService.setAuthToken(response.agent.accessToken, response.agent.refreshToken);
                return response;
            })
        );
    }

    public verifyMemberToken(token: string): Observable<BaseMemberSessionList> {
        const serializeToken = this.apiService.serializeString(token);
        return this.apiService.get(`${memberControllerRoute}/${serializeToken}/verify`).pipe(
            map((response: BaseMemberSessionList) => {
                this.apiService.setAuthToken(response.members[0].accessToken, response.members[0].refreshToken);
                return response;
            })
        );
    }

    // this is required after reset memberMemberPassword is authorized
    public resetMemberLogin(baseMemberSession: BaseMemberSession, version: string): Observable<MemberSession> {
        const payload: LoginPayload = new LoginPayload();
        payload.systemNumber = baseMemberSession.systemNumber;
        payload.adminDirectSystem = this.adminDirectSystemLocation.MyWorkplace;
        payload.buildNumber = version;
        payload.languagePreference = this.sessionService.session.language;
        return this.apiService.post(`${controllerRoute}/select-member-login`, payload).pipe(
            map((response: StartSelectedMemberSession) => {
                if (response.accessToken)
                    this.apiService.refreshTokens(response.accessToken, response.refreshToken);
                let l_MemberSession = new MemberSession;
                l_MemberSession = this.helperService.convertObjectIntoObject(response.session, l_MemberSession);
                l_MemberSession = this.helperService.convertObjectIntoObject(baseMemberSession, l_MemberSession);
                this.loginMember(l_MemberSession);
                return response.session;
            }),
        );
    }

    public memberLogin(payload: LoginPayload): Observable<MemberAuthorizationResult> {
        return from(this.encryptionService.encryptPassword(payload.password)).pipe(
            switchMap((encryptedPassword: PassEncryption) => {
                payload.password = null;
                payload.passEncryption.a1 = encryptedPassword.a1
                payload.passEncryption.m1 = encryptedPassword.m1
                payload.passEncryption.m2 = encryptedPassword.m2
                payload.languagePreference = this.sessionService.session.language;
                return this.apiService.post(`${controllerRoute}/credentials`, payload).pipe(
                    map((memberAuthorizationResult: MemberAuthorizationResult) => {
                        this.apiService.setAuthToken(memberAuthorizationResult.members[0].accessToken, memberAuthorizationResult.members[0].refreshToken);
                        if (memberAuthorizationResult.isMemberAutoLoggedIn) {
                            this.apiService.startTimer(new Date());
                            memberAuthorizationResult.startSessionModel.memberName = memberAuthorizationResult.members[0].memberName;
                            memberAuthorizationResult.startSessionModel.companyName = memberAuthorizationResult.members[0].companyName;
                            memberAuthorizationResult.startSessionModel.systemNumber = memberAuthorizationResult.members[0].systemNumber;
                            this.loginMember(memberAuthorizationResult.startSessionModel);
                        }
                        return memberAuthorizationResult;
                    }));
            })
        )
    }

    public mfaSendCode(payload: AuthenticatorRequest): Observable<Authenticator> {
        return this.apiService.post(`${controllerRoute}/mfa-send-code`, payload).pipe(
            map((response: Authenticator) => {
                return response;
            }),
        );
    }

    public mfaGenerateQR(): Observable<AuthenticatorQR> {
        return this.apiService.get(`${controllerRoute}/mfa-generate-qr`).pipe(
            map((response: AuthenticatorQR) => {
                return response;
            }),
        );
    }

    public mfaSetQR(payload: AuthenticatorQR): Observable<null> {
        return this.apiService.post(`${controllerRoute}/mfa-set-qr`, payload).pipe(
            map((response: null) => {
                return null;
            }),
        );
    }

    public mfaSetCellphoneSendCode(payload: AuthenticatorRequest): Observable<Authenticator> {
        return this.apiService.post(`${controllerRoute}/mfa-set-cellphone-send-code`, payload).pipe(
            map((response: Authenticator) => {
                return response;
            }),
        );
    }

    public mfaVerifyCode(payload: AuthenticatorRequest): Observable<Authenticator> {
        return this.apiService.post(`${controllerRoute}/mfa-verify-code`, payload).pipe(
            map((response: Authenticator) => {
                return response;
            }),
        );
    }

    public mfaMemberVerifyLogin(payload: AuthenticatorRequest): Observable<MemberAuthorizationResult> {
        return this.apiService.post(`${controllerRoute}/mfa-member-verify-auth`, payload).pipe(
            map((response: MemberAuthorizationResult) => {
                if (response.isCodeVerfied) {
                    if (response.isMemberAutoLoggedIn) {
                        this.apiService.startTimer(new Date());
                        response.startSessionModel.memberName = response.members[0].memberName;
                        response.startSessionModel.companyName = response.members[0].companyName;
                        response.startSessionModel.systemNumber = response.members[0].systemNumber;
                        this.loginMember(response.startSessionModel);
                    }
                } else {
                    this.toast.buildToastMessage(ToastType.error, 'Verify Error', response.errorMessage);
                }
                return response;
            }),
        );
    }

    public mfaAgentVerifyLogin(payload: AuthenticatorRequest, mfa: MultiFactorAuthenticator): Observable<Agent> {
        return this.apiService.post(`${controllerRoute}/mfa-agent-verify-auth`, payload).pipe(
            map((response: Agent) => {
                if (response.isCodeVerfied) {
                    response.adminDirectSystem = mfa.adminDirectSystem;
                    response.agentCity = mfa.agentCity;
                    response.agentState = mfa.agentState;
                    this.agentLoginVerified(response, false);
                } else {
                    this.toast.buildToastMessage(ToastType.error, 'Verify Error', response.errorMessage);
                }
                return response;
            }),
        );
    }

    public selectMemberLogin(payload: LoginPayload, baseMemberSession: BaseMemberSession): Observable<StartSelectedMemberSession> {
        this.apiService.startTimer(new Date());
        payload.languagePreference = this.sessionService.session.language;
        return this.apiService.post(`${controllerRoute}/select-member-login`, payload).pipe(
            map((response: StartSelectedMemberSession) => {
                if (response.accessToken) {
                    this.apiService.setAuthToken(response.accessToken, response.refreshToken);
                }
                response.session.memberName = baseMemberSession.memberName;
                response.session.companyName = baseMemberSession.companyName;
                response.session.systemNumber = baseMemberSession.systemNumber;
                this.loginMember(response.session);
                return response;
            }));
    }

    private loginMember(memberSession: MemberSession): void {
        this.sessionService.session.isMember = true;
        this.sessionService.session.isLoggedIn = true;
        this.sessionService.session.member = memberSession;
        this.sessionService.session.member.memberName = memberSession.memberName;
        this.sessionService.session.member.companyName = memberSession.companyName;
        this.sessionService.session.member.systemNumber = memberSession.systemNumber;
        this.sessionService.session.member.usePayrollModule = memberSession.usePayrollModule;
        this.sessionService.session.member.useYearRound = memberSession.useYearRound;
        this.sessionService.session.member.useACAModule = memberSession.useACAModule;
        this.sessionService.session.member.languagePreference = memberSession.languagePreference
        this.sessionService.session.member.firstName = memberSession.firstName;

        if (!this.sessionService.session.isAgent && this.sessionService.session.member.languagePreference)
            this.sessionService.session.language = this.sessionService.session.member.languagePreference;

        this.sessionService.setSession();
    }

    public validateEmailPassword(payload: LoginPayload): Observable<BaseMemberSessionList> {
        return from(this.encryptionService.encryptPassword(payload.password)).pipe(
            switchMap((encryptedPassword: PassEncryption) => {
                payload.password = null;
                payload.passEncryption.a1 = encryptedPassword.a1
                payload.passEncryption.m1 = encryptedPassword.m1
                payload.passEncryption.m2 = encryptedPassword.m2
                return this.apiService.post(`${controllerRoute}/validate-email`, payload).pipe(
                    map(response => response as BaseMemberSessionList));
            }))
    }

    public registerMember(payload: RegisterCriteria, isResetRequest: boolean): Observable<AuthenticatorInfo> {
        const l_PathPreFix = isResetRequest ? "reset-request" : "register";
        return this.apiService.post(`${controllerRoute}/${l_PathPreFix}`, payload).pipe(map((response: AuthenticatorInfo) => {
            this.apiService.refreshTokens(response.accessToken, null);
            return response;
        }));
    }

    public registerVerifyEmail(payload: AuthenticatorRequest, isResetRequest: boolean): Observable<Authenticator> {
        const l_PathPreFix = isResetRequest ? "reset-request" : "register";
        return this.apiService.post(`${controllerRoute}/${l_PathPreFix}-verify-email`, payload).pipe(
            map((response: Authenticator) => {
                return response;
            }));
    }

    public registerResendEmailCode(payload: AuthenticatorRequest, isResetRequest: boolean): Observable<Authenticator> {
        const l_PathPreFix = isResetRequest ? "reset-request" : "register";
        return this.apiService.post(`${controllerRoute}/${l_PathPreFix}-resend-email-code`, payload).pipe(
            map((response: Authenticator) => {
                return response;
            }));
    }

    public registerMFA(payload: AuthenticatorRequest, isResetRequest: boolean): Observable<Authenticator> {
        const l_PathPreFix = isResetRequest ? "reset-request" : "register";
        return this.apiService.post(`${controllerRoute}/${l_PathPreFix}-mfa`, payload).pipe(
            map((response: Authenticator) => {
                return response;
            }));
    }

    public registerValidateMFA(payload: AuthenticatorRequest, isResetRequest: boolean): Observable<Authenticator> {
        const l_PathPreFix = isResetRequest ? "reset-request" : "register";
        return this.apiService.post(`${controllerRoute}/${l_PathPreFix}-validate-mfa`, payload).pipe(
            map((response: Authenticator) => {
                return response;
            }));
    }

    public registerPassword(payload: LoginPayload, isResetRequest: boolean): Observable<MemberAuthorizationResult> {
        const l_PathPreFix = isResetRequest ? "reset-request" : "register";
        return from(this.encryptionService.encryptPassword(payload.password)).pipe(
            switchMap((encryptedPassword: PassEncryption) => {
                payload.password = null;
                payload.passEncryption.a1 = encryptedPassword.a1
                payload.passEncryption.m1 = encryptedPassword.m1
                payload.passEncryption.m2 = encryptedPassword.m2
                return this.apiService.post(`${controllerRoute}/${l_PathPreFix}-password`, payload).pipe(
                    map((response: MemberAuthorizationResult) => {
                        if (response.isMemberAutoLoggedIn) {
                            this.apiService.startTimer(new Date());
                            response.startSessionModel.memberName = response.members[0].memberName;
                            response.startSessionModel.companyName = response.members[0].companyName;
                            response.startSessionModel.systemNumber = response.members[0].systemNumber;

                            this.apiService.setAuthToken(response.members[0].accessToken, response.members[0].refreshToken);
                            this.loginMember(response.startSessionModel);
                        }
                        return response;
                    }));
            })
        )
    }

    public registerSelectedMember(payload: RegisterCriteria, isResetRequest: boolean): Observable<MemberAuthorizationResult> {
        const l_PathPreFix = isResetRequest ? "reset-request" : "register";
        return this.apiService.post(`${controllerRoute}/${l_PathPreFix}-selected-member`, payload).pipe(
            map((response: MemberAuthorizationResult) => {
                this.apiService.startTimer(new Date());
                response.startSessionModel.memberName = response.members[0].memberName;
                response.startSessionModel.companyName = response.members[0].companyName;
                response.startSessionModel.systemNumber = response.members[0].systemNumber;

                this.apiService.setAuthToken(response.members[0].accessToken, response.members[0].refreshToken);
                this.loginMember(response.startSessionModel);
                return response;
            }));
    }

    public deleteAccount(): Observable<any> {
        return this.apiService.delete(`${controllerRoute}/delete-member-profile`).pipe(
            map((response: any) => {
                this.sessionService.logout();
                this.apiService.cancelTimer();
                this.router.navigateByUrl(BasePaths.AccountDeleted);
                this.toast.buildToastMessage(ToastType.warning, "Account Deleted", "You have successfully deleted your account");
                return null;
            }),
        );
    }

    public passthroughLogin(payload: PassthroughBearTokenPayload): Observable<MemberSession> {
        return this.apiService.post(`${controllerRoute}/credentials/pass-through`, payload).pipe(
            map((response: MemberSession) => {
                this.apiService.setAuthToken(response.accessToken, response.refreshToken);
                this.sessionService.session.isMember = true;
                this.sessionService.session.isAdmin = true;
                this.sessionService.session.member = response;
                this.sessionService.session.member.memberName = response.memberName;
                this.sessionService.session.member.companyName = response.companyName;
                this.sessionService.session.member.systemNumber = response.systemNumber;
                this.sessionService.session.adminUserName = response.adminUserName;
                this.sessionService.session.isLoggedIn = true;
                this.sessionService.setSession();
                return response;
            }),
            catchError((error) => {
                this.toast.buildToastMessage(ToastType.error, 'Error', 'Failed Passthrough Authentication');
                this.router.navigateByUrl(BasePaths.Members);
                throw error;
            }));
    }

    public refreshToken(): Observable<IdentityServerGrantBase> {
        const l_Payload = new IdentityServerRefresh();
        l_Payload.refreshToken = this.apiService.apiStatus.refreshToken;
        return this.apiService.post(`${controllerRoute}/refresh-auth-token`, l_Payload).pipe(
            map((response: IdentityServerGrantBase) => {
                if (response.isSuccess) {
                    this.apiService.refreshTokens(response.accessToken, response.refreshToken);
                } else {
                    this.logout();
                }
                return response;
            }),
        );
    }

    public refreshSession(): Observable<null> {
        const l_Payload = new IdentityServerRefresh();
        l_Payload.refreshToken = this.apiService.apiStatus.refreshToken;
        return this.apiService.post(`${controllerRoute}/refresh-session`, l_Payload).pipe(
            map((response: IdentityServerGrantBase) => {
                if (response.isSuccess) {
                    this.apiService.refreshTokens(response.accessToken, response.refreshToken);
                } else {
                    this.logout();
                }
                return null;
            }),
        );
    }
}